import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { ExpensesDataCollection } from 'modules/expenses/components/expenses/DataCollection/ExpensesDataCollection'
import { ExpensesDataTable } from 'modules/expenses/components/expenses/DataTable'
import { useQuickFiltersContext } from 'modules/expenses/components/expenses/PageContents/QuickFiltersContext'
import { useExpensesListPage } from 'modules/expenses/hooks/useExpensesListPage'
import { Group } from 'modules/expenses/hooks/useGroup'
import { PageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'
import expensesCategoriesContractsTaxonomies from 'modules/payroll_bundle/store/collections/expensesCategoriesContractsTaxonomies'
import policyPeriods from 'modules/payroll_bundle/store/collections/policyPeriods'

export interface ListProps {
  group?: Group
  employeeIds?: number[]
  back: { href: string }
  onAddUsers?: () => void
  addUsersDisabled?: boolean
  dateFilters?: PageDateFilters
  setPageDateFilters?: (startDate: Date, endDate: Date) => void
}

const List = ({
  group,
  employeeIds,
  back,
  onAddUsers,
  addUsersDisabled,
  dateFilters,
  setPageDateFilters,
}: React.PropsWithChildren<ListProps>) => {
  const [availableCategories, setAvailableCategories] = React.useState<string[] | undefined>(
    undefined
  )
  const [currentPeriodOpen, setCurrentPeriodOpen] = React.useState<boolean>(false)
  const { filters } = useQuickFiltersContext()
  const { company } = useCurrentSession()

  const {
    data,
    featureAvailabilities: { dataCollectionEnabled },
  } = useExpenses()

  const {
    categories,
    subcategories,
    subprojects,
    canSeeTransactions,
    hasExpensesReportOnBehalfFeature,
    hasExpensesPerDiemsFeature,
    loading,
  } = useExpensesListPage({ companyId: company.id })

  const listedCategories = hasExpensesPerDiemsFeature
    ? categories
    : categories.filter((category) => category.identifier !== 'perdiem')

  const currencies = data?.apiCore?.currencies ?? []
  const { loading: loadingPayroll } = useAsyncData(
    () =>
      Promise.all([
        expensesCategoriesContractsTaxonomies.existingTaxonomies().then((payrollCategories) => {
          setAvailableCategories(payrollCategories)
        }),
        policyPeriods.isCurrentOpen().then(({ open_to_supplements }) => {
          setCurrentPeriodOpen(open_to_supplements)
        }),
      ]),
    []
  )

  if (loading || loadingPayroll) {
    return (
      <Box paddingY="s32" paddingX="s48" justifyContent="center" alignItems="center" width="full">
        <Spinner />
      </Box>
    )
  }

  return dataCollectionEnabled ? (
    <ExpensesDataCollection
      back={back}
      companyId={company.id}
      canSeeTransactions={canSeeTransactions}
      onAddUsers={onAddUsers}
      currentPayrollPeriodOpen={currentPeriodOpen}
      availablePayrollCategories={availableCategories || []}
    />
  ) : (
    <ExpensesDataTable
      companyId={company.id}
      key={JSON.stringify(filters)} //re-mount the datatable if expensable search params change
      group={group}
      subcategories={subcategories}
      currencies={currencies}
      subprojects={subprojects}
      currentPayrollPeriodOpen={currentPeriodOpen}
      availablePayrollCategories={availableCategories || []}
      categories={listedCategories}
      employeeIds={employeeIds}
      back={back}
      hasReportOnBehalfFeature={hasExpensesReportOnBehalfFeature}
      canSeeTransactions={canSeeTransactions}
      canRestartApprovalFlow={data.expenses._policies.expenseRestartApprovalFlow.can}
      canSetExpensableToPaid={data.expenses._policies.expenseSetToPaid.can}
      onAddUsers={onAddUsers}
      addUsersDisabled={addUsersDisabled}
      dateFilters={dateFilters}
      setPageDateFilters={setPageDateFilters}
    />
  )
}

export default observer(List)
